import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { webTapFloor, type UiTheme } from "../theme";

export type DiaryMode = "list" | "calendar";

// #368: List | Calendar as one joined two-cell control (mockup .seg). On: the
// surface2 cell, weight 700 and a short rule under the label, so the state is
// never the fill alone. Off while selecting: an opaque surface and ink2 text,
// not an opacity on the brand. One callsite, so it lives here, not in
// primitives.
export function DiaryModeSwitch({
  theme,
  mode,
  disabled,
  onChange,
}: {
  theme: UiTheme;
  mode: DiaryMode;
  disabled: boolean;
  onChange: (m: DiaryMode) => void;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  return (
    <View style={styles.seg} accessibilityRole="tablist">
      {(["list", "calendar"] as const).map((m, i) => {
        const on = mode === m;
        return (
          <Pressable
            key={m}
            accessibilityRole="tab"
            accessibilityState={{ selected: on, disabled }}
            disabled={disabled}
            onPress={() => onChange(m)}
            style={[styles.cell, i > 0 && styles.divider, on && styles.on, disabled && styles.off]}
          >
            <View style={styles.labelBox}>
              <Text style={[styles.label, on && styles.labelOn]}>
                {m === "list" ? t("mobile.workouts.modeList") : t("mobile.workouts.modeCalendar")}
              </Text>
              {on ? <View style={styles.rule} /> : null}
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    seg: {
      flex: 1,
      flexDirection: "row",
      borderWidth: 1,
      borderColor: p.borderStrong,
      borderRadius: r.r2,
      overflow: "hidden",
      backgroundColor: p.surface,
    },
    cell: {
      flex: 1,
      minHeight: webTapFloor ?? 44,
      paddingHorizontal: s.s4,
      alignItems: "center",
      justifyContent: "center",
    },
    divider: { borderLeftWidth: 1, borderLeftColor: p.borderStrong },
    on: { backgroundColor: p.surface2 },
    // Opaque and legible: ink2 on surface/surface2, no opacity.
    off: { backgroundColor: p.surface },
    labelBox: { alignSelf: "stretch", alignItems: "center", paddingBottom: s.s1 },
    label: { textAlign: "center", color: p.ink2, fontSize: ty.sizes.base, fontWeight: "500" },
    labelOn: { color: p.ink, fontWeight: "700" },
    rule: { alignSelf: "stretch", height: 2, marginTop: s.s1, marginHorizontal: s.s3, backgroundColor: p.ink },
  });
}
